import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, inject } from '@angular/core';
import * as i2 from '@angular/router';
import { RouterModule, Router } from '@angular/router';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { appendRequestCacheHeaders, TRACE_HEADER } from '@psu/utils/browser';
import { httpRetryBackoff } from '@psu/utils/rx';
import { REQUIRE_AUTH_HEADER } from '@psu/utils/security';
import { of, map } from 'rxjs';
import { tap, take, filter, map as map$1 } from 'rxjs/operators';
import { any } from 'ramda';

var LoadFortressData;
(function (LoadFortressData) {
    LoadFortressData[LoadFortressData["Permissions"] = 0] = "Permissions";
    LoadFortressData[LoadFortressData["Roles"] = 1] = "Roles";
    LoadFortressData[LoadFortressData["Both"] = 2] = "Both";
})(LoadFortressData || (LoadFortressData = {}));
const FORTRESS_CONFIG = new InjectionToken('psu.apis.fortress.config');
// Default route used when PermissionGuard disallows navigation.
const NO_PERMISSION_ROUTE = 'no-permissions';
/**
 * Factory function to wire up the no permission route with a given component.
 *
 * @param component the component type to show when routing to no permissions
 */
const configureNoPermissionsRoute = (component) => ({
    path: NO_PERMISSION_ROUTE,
    component
});
const importNoPermissionsRoute = (component) => RouterModule.forChild([configureNoPermissionsRoute(component)]);

const usersPath = 'users';
const permissionsPath = 'permissions';
const rolesPath = 'roles';
const appendHeaders = (serviceConfig, headers) => {
    let requestHeaders = headers;
    if (serviceConfig) {
        if (serviceConfig.cacheOptions) {
            requestHeaders = appendRequestCacheHeaders(serviceConfig.cacheOptions, requestHeaders);
        }
    }
    return requestHeaders;
};
class FortressService {
    constructor(httpClient, fortressConfig) {
        this.httpClient = httpClient;
        this.fortressConfig = fortressConfig;
        this.headers = new HttpHeaders().append(REQUIRE_AUTH_HEADER, 'true');
        if (this.fortressConfig.enableTracing) {
            this.headers = this.headers.append(TRACE_HEADER, 'true');
        }
    }
    getUserPermissions(accessId, realm = 'access', permOu, serviceConfig) {
        if (!accessId || !realm) {
            throw new Error('FortressService.getUserPermissions requires a userId and a realm: ' + accessId + ', ' + realm);
        }
        const url = this.getUrl(accessId, realm, permissionsPath);
        const options = {
            headers: appendHeaders(serviceConfig, this.headers),
            params: this.getParams(permOu),
        };
        return this.httpClient.get(url, options).pipe(httpRetryBackoff(250));
    }
    getUserRoles(accessId, realm = 'access', permOu, name, serviceConfig) {
        if (!accessId || !realm) {
            throw new Error('FortressService.getUserRoles requires a userId and a realm: ' + accessId + ', ' + realm);
        }
        return this.httpClient
            .get(this.getUrl(accessId, realm, rolesPath), {
            headers: appendHeaders(serviceConfig, this.headers),
            params: this.getParams(permOu, name),
        })
            .pipe(httpRetryBackoff(250));
    }
    getMyUserPermissions(permOu, activatedRoles, serviceConfig) {
        return this.httpClient
            .get(`${this.fortressConfig.url}${permissionsPath}/me`, {
            headers: appendHeaders(serviceConfig, this.headers),
            params: this.getParams(permOu, undefined, activatedRoles),
        })
            .pipe(httpRetryBackoff(250));
    }
    getMyUserRoles(permOu, name, serviceConfig) {
        return this.httpClient
            .get(`${this.fortressConfig.url}${rolesPath}/me`, {
            headers: appendHeaders(serviceConfig, this.headers),
            params: this.getParams(permOu, name),
        })
            .pipe(httpRetryBackoff(250));
    }
    getUrl(accessId, realm, path) {
        return `${this.fortressConfig.url}${usersPath}/${accessId}_${realm}/${path}`;
    }
    getParams(permOu, name, activatedRoles) {
        let params = {};
        if (permOu && permOu.length > 0) {
            params = { ...params, permOu };
        }
        if (name) {
            params = { ...params, name };
        }
        if (activatedRoles && activatedRoles.length > 0) {
            params = { ...params, activatedRoles };
        }
        return new HttpParams({ fromObject: params });
    }
}
FortressService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: FortressService, deps: [{ token: i1.HttpClient }, { token: FORTRESS_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
FortressService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: FortressService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: FortressService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FORTRESS_CONFIG]
                }] }]; } });

/**
 * This is a "class interface" for a service that checks permissions.
 */
class PermissionCheckService {
}

/**
 * @deprecated Use the guard functions from {@link PermissionGuards} or {@link RoleGuards}
 */
class BasePermissionGuard {
    constructor(permissionService, router) {
        this.permissionService = permissionService;
        this.router = router;
    }
    canActivate(route, state) {
        this.activatedRouteSnapshot = route;
        this.routerStateSnapshot = state;
        return this.checkPermissions(route.data);
    }
    canLoad(route) {
        this.route = route;
        return this.checkPermissions(route?.data);
    }
    /**
     * Navigates the user away from this route.  Called if the user is missing a required permission.
     *
     * Subclasses may override this method and implement their own navigation logic.
     */
    navigateAway() {
        this.router.navigate([NO_PERMISSION_ROUTE], {
            skipLocationChange: true,
        });
    }
    checkPermissions(data) {
        // if the route defines no data, there are no roles or permissions to check, return true.
        if (!data) {
            return of(true);
        }
        const roles = data.roles;
        const permissions = data.permissions;
        let ret;
        if (!roles && !permissions) {
            ret = of(true);
        }
        else {
            ret = this.doAccessCheck(data);
        }
        return ret.pipe(tap((hasAccess) => {
            if (!hasAccess) {
                this.navigateAway();
            }
        }), take(1));
    }
}

/**
 * @deprecated Use the guard functions from {@link PermissionGuards}
 */
class PermissionGuard extends BasePermissionGuard {
    constructor(permissionService, router) {
        super(permissionService, router);
        this.permissionService = permissionService;
        this.router = router;
    }
    doAccessCheck(data) {
        const permissions = data.permissions;
        const matchAll = !!data.matchAll;
        const activatedRoles = !!data.roles ? (data.roles instanceof Array ? data.roles : [data.roles]) : [];
        return this.permissionService.hasPermission(permissions || [], matchAll, activatedRoles);
    }
}
PermissionGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PermissionGuard, deps: [{ token: PermissionCheckService }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Injectable });
PermissionGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PermissionGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: PermissionGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: PermissionCheckService }, { type: i2.Router }]; } });
/**
 * Checks if the current user has the specified permission(s).
 *
 * @param permissions One or more permission names
 * @param matchAll Whether to require the user to have all specified permissions or at least one
 * @param redirectPath A route path to redirect to in the event that the user does not have the specified permissions
 * @param activatedRoles list of role names to limit permission checks to the set of permissions among the listed roles
 * @returns An Observable which emits one of the following:
 *  - `true` if the user has the specified permissions
 *  - A `UrlTree` if the user does not have the specified permissions and a `redirectPath` has been specified
 *  - `false` otherwise
 */
const checkPermissions = (permissions, matchAll = false, redirectPath, activatedRoles) => {
    const permissionService = inject(PermissionCheckService);
    const router = inject(Router);
    return permissionService.hasPermission(permissions, matchAll, activatedRoles).pipe(map((hasPermission) => {
        if (hasPermission) {
            return true;
        }
        else {
            if (redirectPath) {
                router.navigate([NO_PERMISSION_ROUTE], {
                    skipLocationChange: true,
                });
            }
            return false;
        }
    }));
};
/**
 * Checks if the current user has the specified permission(s).
 *
 * @param permissions One or more permission names
 * @param matchAll Whether to require the user to have all specified permissions or at least one (default: false)
 * @param redirectPath A route path to redirect to in the event that the user does not have the specified permissions;
 *    if undefined, no redirection occurs
 * @param activatedRoles list of role names to limit permission checks to the set of permissions among the listed roles
 * @returns `CanActivateFn`
 */
const canActivate$1 = (permissions, matchAll = false, redirectPath, activatedRoles) => (route, state) => checkPermissions(permissions, matchAll, redirectPath, activatedRoles);
/**
 * Checks if the current user has the specified permission(s).
 *
 * @param permissions One or more permission names
 * @param matchAll Whether to require the user to have all specified permissions or at least one
 * @param redirectPath A route path to redirect to in the event that the user does not have the specified permissions;
 *    if undefined, no redirection occurs
 * @param activatedRoles list of role names to limit permission checks to the set of permissions among the listed roles
 * @returns `CanActivateChildFn`
 */
const canActivateChild$1 = (permissions, matchAll = false, redirectPath, activatedRoles) => (childRoute, state) => checkPermissions(permissions, matchAll, redirectPath, activatedRoles);
/**
 * Checks if the current user has the specified permission(s).
 *
 * @param permissions One or more permission names
 * @param matchAll Whether to require the user to have all specified permissions or at least one
 * @param redirectPath A route path to redirect to in the event that the user does not have the specified permissions;
 *    if undefined, no redirection occurs
 * @param activatedRoles list of role names to limit permission checks to the set of permissions among the listed roles
 * @returns `CanMatchFn`
 */
const canMatch$1 = (permissions, matchAll = false, redirectPath, activatedRoles) => (route, segments) => checkPermissions(permissions, matchAll, redirectPath, activatedRoles);
/**
 * Functional router guards to check if the current user has the specified permission(s).
 */
const PermissionGuards = {
    canActivate: canActivate$1,
    canActivateChild: canActivateChild$1,
    canMatch: canMatch$1,
};

/**
 * @deprecated Use the guard functions from {@link RoleGuards}
 */
class RoleGuard extends BasePermissionGuard {
    constructor(permissionService, router) {
        super(permissionService, router);
        this.permissionService = permissionService;
        this.router = router;
    }
    doAccessCheck(data) {
        const roles = data.roles;
        const matchAll = !!data.matchAll;
        return this.permissionService.hasRole(roles || [], matchAll);
    }
}
RoleGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: RoleGuard, deps: [{ token: PermissionCheckService }, { token: i2.Router }], target: i0.ɵɵFactoryTarget.Injectable });
RoleGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: RoleGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: RoleGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: PermissionCheckService }, { type: i2.Router }]; } });
/**
 * Checks if the current user has the specified role(s).
 *
 * @param roles One or more role names
 * @param matchAll Whether to require the user to have all specified roles or at least one
 * @param redirectPath A route path to redirect to in the event that the user does not have the specified roles
 * @returns An Observable which emits one of the following:
 *  - `true` if the user has the specified roles
 *  - A `UrlTree` if the user does not have the specified roles and a `redirectPath` has been specified
 *  - `false` otherwise
 */
const checkRoles = (roles, matchAll = false, redirectPath) => {
    const permissionService = inject(PermissionCheckService);
    const router = inject(Router);
    return permissionService.hasRole(roles, matchAll).pipe(map((hasRole) => {
        if (hasRole) {
            return true;
        }
        else if (redirectPath) {
            return router.parseUrl(redirectPath);
        }
        else {
            return false;
        }
    }));
};
/**
 * Checks if the current user has the specified role(s).
 *
 * @param roles One or more role names
 * @param matchAll Whether to require the user to have all specified roles or at least one (default: false)
 * @param redirectPath A route path to redirect to in the event that the user does not have the specified roles;
 *    if undefined, no redirection occurs
 * @returns `CanActivateFn`
 */
const canActivate = (roles, matchAll = false, redirectPath) => (route, state) => checkRoles(roles, matchAll, redirectPath);
/**
 * Checks if the current user has the specified role(s).
 *
 * @param roles One or more role names
 * @param matchAll Whether to require the user to have all specified roles or at least one
 * @param redirectPath A route path to redirect to in the event that the user does not have the specified roles;
 *    if undefined, no redirection occurs
 * @returns `CanActivateChildFn`
 */
const canActivateChild = (roles, matchAll = false, redirectPath) => (childRoute, state) => checkRoles(roles, matchAll, redirectPath);
/**
 * Checks if the current user has the specified role(s).
 *
 * @param roles One or more role names
 * @param matchAll Whether to require the user to have all specified roles or at least one
 * @param redirectPath A route path to redirect to in the event that the user does not have the specified roles;
 *    if undefined, no redirection occurs
 * @returns `CanMatchFn`
 */
const canMatch = (roles, matchAll = false, redirectPath) => (route, segments) => checkRoles(roles, matchAll, redirectPath);
/**
 * Functional router guards to check if the current user has the specified role(s).
 */
const RoleGuards = {
    canActivate,
    canActivateChild,
    canMatch,
};

const fortressServiceConfig = {
    cacheOptions: {
        cacheRequestMinutes: '60',
    },
};
/**
 * Service that lets you check permissions/roles for a user based on the service.
 */
class UserPermissionCheckService extends PermissionCheckService {
    constructor(fortressService) {
        super();
        this.fortressService = fortressService;
        /**
         * Check whether the current user in the store has the given permission(s).
         *
         * @param name the permission abstract name, or an array of permission abstract names
         * @param matchAll whether to require all permissions (true), or just one (false).  Defaults to false
         * @param activatedRoles list of role names to limit permission checks to the set of permissions among the listed roles
         * @param permOus list of service names to limit permission checks to a specific service.
         *  - For Java projects, you can find the permOu name by searching for the `@ApplicationName` annotation.
         *  - For golang services, all permission-related information can be found in the security directory. Alternatively,
         *  you can find the `<servicename>-fortresspermissions.yaml` in the deployment configuration repository.
         */
        this.hasPermission = (name, matchAll, activatedRoles, permOus) => {
            const requiredPermissions = (name instanceof Array ? name : [name]).filter((n) => !!(n || '').trim());
            if (requiredPermissions.length === 0) {
                return of(true);
            }
            return this.fortressService.getMyUserPermissions(permOus, activatedRoles, fortressServiceConfig).pipe(filter((permissions) => permissions !== undefined), map$1((permissions) => matchAll
                ? requiredPermissions.every((abstractName) => this.checkForAbstractName(abstractName, permissions))
                : requiredPermissions.some((abstractName) => this.checkForAbstractName(abstractName, permissions))));
        };
        /**
         * Check whether the current user in the store has the given role.
         *
         * @param name the role name, or an array of role names
         * @param matchAll whether to require all roles (true), or just one (false).  Defaults to false
         */
        this.hasRole = (name, matchAll = false) => {
            const requiredRoles = (name instanceof Array ? name : [name]).filter((n) => !!(n || '').trim());
            if (requiredRoles.length === 0) {
                return of(true);
            }
            return this.fortressService.getMyUserRoles(null, null, fortressServiceConfig).pipe(filter((roles) => roles !== undefined), map$1((roles) => matchAll
                ? requiredRoles.every((roleName) => this.checkForUserRoleName(roleName, roles))
                : requiredRoles.some((roleName) => this.checkForUserRoleName(roleName, roles))));
        };
    }
    checkForAbstractName(abstractName, permissions) {
        const hasAn = (perms) => perms.abstractName === abstractName;
        return any(hasAn)(permissions);
    }
    checkForUserRoleName(roleName, roles) {
        const hasAn = (r) => r.name === roleName;
        return any(hasAn)(roles);
    }
}
UserPermissionCheckService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: UserPermissionCheckService, deps: [{ token: FortressService }], target: i0.ɵɵFactoryTarget.Injectable });
UserPermissionCheckService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: UserPermissionCheckService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.9", ngImport: i0, type: UserPermissionCheckService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: FortressService }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { FORTRESS_CONFIG, FortressService, LoadFortressData, NO_PERMISSION_ROUTE, PermissionCheckService, PermissionGuard, PermissionGuards, RoleGuard, RoleGuards, UserPermissionCheckService, configureNoPermissionsRoute, importNoPermissionsRoute };
